// combos.js
// Shared combo + add-on definitions for the upsell system.
//
// ── The model ────────────────────────────────────────────────────────────────
// A combo is ONE pancake + physical goods (kit, print) riding the SAME order
// doc — deliberately NOT a basket (baskets = multiple pancakes; conflating
// them would tangle the double-serve guard). The goods become fulfillment
// obligations: `order.comboKey` + `order.addOns[]` ({type, fulfilledAt}).
// Badges ride the queue cards, delivery is the checkpoint, shift close guards
// against forgotten add-ons.
//
// PRICING IS DISPLAY-ONLY HERE. The server (functions/index.js COMBO_DEFS)
// is the sole pricing authority for anything payable — same rule as /join
// tiers. Keep the two tables in sync when prices change.
//
// Per-event activation: `shift.activeComboKeys[]` (seeded by openShift from
// Event Config `shiftDefaults.activeComboKeys`, live-editable in 🎛 Shift
// Controls). Party/free voice shows NO combos anywhere — party guests never
// see commerce.

export const CUSTOM_BASE_CENTS = 3000; // 📷 custom tier — the combo base
export const SOUVENIR_PRINT_CENTS = 1000; // hiprint print of your pancake

export const ADD_ONS = {
  preservation_kit: {
    type: "preservation_kit",
    label: "Preservation Kit",
    short: "Kit",
    emoji: "🎁",
    fulfillHint: "Hand over a preservation kit",
  },
  display_frame: {
    type: "display_frame",
    label: "Display Frame",
    short: "Frame",
    emoji: "🖼️",
    fulfillHint: "Hand over a display frame",
  },
  signed_print: {
    type: "signed_print",
    label: "Signed Print",
    short: "Print",
    emoji: "✍️",
    fulfillHint: "Print, sign + number their pancake photo",
  },
  // Road Mode extra ("Make It a Dessert") — sauced before hand-off, so it
  // rides the same badge + delivery-checkpoint rails as physical goods.
  toppings: {
    type: "toppings",
    label: "Dessert Toppings",
    short: "Sauce",
    emoji: "🍫",
    fulfillHint: "Sauce it — chocolate / caramel / pistachio crème / syrup (+ compote while it lasts)",
  },
};

export const COMBO_ORDER = ["artist", "collector"];

export const COMBOS = {
  artist: {
    key: "artist",
    label: "Artist Combo",
    emoji: "🎨",
    priceCents: 4500,
    // True à-la-carte sum ($30 custom + $15 kit + $5 frame) — strikethroughs
    // only render when this exceeds priceCents, so equal = no discount shown.
    alaCarteCents: 5000,
    baseOrderType: "custom",
    // Full combos include the display frame (Dan, 2026-08-12) — that's where
    // the real bundle savings come from. The bare goods-mode kit does NOT
    // (it IS the standalone $15 kit; the frame stays the +$5 booth modifier).
    addOns: ["preservation_kit", "display_frame"],
    pitch: "Your custom pancake + a framed preservation kit to keep it forever.",
    // Goods-only voice (free pancakes, goodsMode "sell"): same bundle minus
    // the pancake base — priced at the upgrade delta.
    goodsLabel: "Preservation Kit",
    // No frame claim — the bare kit sale is frameless (frame = +$5 at booth).
    goodsPitch: "Take tonight's pancake home forever — everything you need to preserve it.",
    goodsAddOns: ["preservation_kit"],
    goodsAlaCarteCents: 1500,
    // Square catalog photo of the real kit (stable CDN URL) — shown on the
    // goods-mode spotlight card.
    goodsImageUrl: "https://items-images-production.s3.us-west-2.amazonaws.com/files/b81eb7ab0618664657726ac285e9dc7535887903/original.jpeg",
  },
  collector: {
    key: "collector",
    label: "Collector's Kit",
    emoji: "🏆",
    priceCents: 5500,
    // $30 custom + $15 kit + $5 frame + $10 print
    alaCarteCents: 6000,
    baseOrderType: "custom",
    addOns: ["preservation_kit", "display_frame", "signed_print"],
    pitch:
      "Custom pancake + framed preservation kit + a signed print stamped with your pancake's official GTS number.",
    goodsLabel: "Collector's Kit",
    goodsPitch: "Framed preservation kit + a signed print stamped with your pancake's official GTS number.",
    goodsAddOns: ["preservation_kit", "display_frame", "signed_print"],
    // $15 kit + $5 frame + $10 print
    goodsAlaCarteCents: 3000,
    goodsImageUrl: "https://items-images-production.s3.us-west-2.amazonaws.com/files/b81eb7ab0618664657726ac285e9dc7535887903/original.jpeg",
  },
};

export function fmtCents(cents) {
  if (typeof cents !== "number") return "";
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

// Upgrade delta for an already-paid custom → combo. Display only — the
// server derives its own delta and Square charges THAT.
export function comboUpgradeDeltaCents(comboKey) {
  const combo = COMBOS[comboKey];
  if (!combo) return null;
  return combo.priceCents - CUSTOM_BASE_CENTS;
}

// The addOns[] array a fresh combo order should carry. Always build through
// this so every writer stamps the same shape. goods = a goods-mode sale
// (free-pancake shift): the artist tier is then the bare frameless kit.
export function comboAddOnsFor(comboKey, { goods = false } = {}) {
  const combo = COMBOS[comboKey];
  if (!combo) return [];
  const types = goods && combo.goodsAddOns ? combo.goodsAddOns : combo.addOns;
  return types.map((type) => ({ type, fulfilledAt: null }));
}

export function unfulfilledAddOns(order) {
  const list = Array.isArray(order?.addOns) ? order.addOns : [];
  return list.filter((a) => a && !a.fulfilledAt);
}

export function addOnLabel(a) {
  const d = ADD_ONS[a?.type];
  return d ? `${d.emoji} ${d.label}` : a?.type || "?";
}

// fulfilledAt is EPOCH MS (client clock) — serverTimestamp() can't live
// inside array elements. Consumers only ever check truthiness.
export function fulfillAllAddOns(order, nowMs = Date.now()) {
  return (Array.isArray(order?.addOns) ? order.addOns : []).map((a) =>
    a && !a.fulfilledAt ? { ...a, fulfilledAt: nowMs } : a,
  );
}

// Delivery checkpoint: confirm text listing what must physically change
// hands, or null when nothing is due. Shared by every "delivered" tap
// (ArtistQueueView tray + CameraView) so the two writers can't drift.
export function addOnDeliveryPrompt(order) {
  const due = unfulfilledAddOns(order);
  if (due.length === 0) return null;
  const combo = COMBOS[order?.comboKey];
  return `${combo ? `${combo.emoji} ${combo.label}` : "🎁 Combo"} — hand over:\n\n${due
    .map((a) => `  ${addOnLabel(a)}`)
    .join("\n")}\n\nOK = delivered + add-ons handed over\nCancel = don't mark delivered yet`;
}

// Which combos are live for this shift, and in which pricing voice.
// Market voice → full combos (pancake included). Free/party voice → NOTHING,
// unless the shift explicitly opts into goods commerce (shift.goodsMode
// "sell" — the door-paid-show format: pancakes free, kits/prints sold), in
// which case entries come back flagged goodsOnly with delta pricing. Legacy
// shifts (no fields) → none.
export function activeCombosForShift(shift) {
  if (!shift) return [];
  const goodsOnly = shift?.qrFlows?.mode === "free";
  if (goodsOnly && shift?.goodsMode !== "sell") return [];
  const keys = Array.isArray(shift.activeComboKeys) ? shift.activeComboKeys : [];
  return COMBO_ORDER.filter((k) => keys.includes(k)).map((k) => ({ ...COMBOS[k], goodsOnly }));
}

// True when this shift sells take-home goods on FREE pancakes (goods-only
// prices, party-safe copy). The one gate every party-voice commerce surface
// must check.
export function shiftSellsGoods(shift) {
  return shift?.qrFlows?.mode === "free" && shift?.goodsMode === "sell";
}
